import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6 } from "styled-components";
import type { Mode } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { Eyebrow } from "~/features/ui/eyebrow";
import { isLinkValid } from "~/features/ui/link/utils";
import { Button, type ButtonVariant } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { takeFirst } from "~/features/app/utils";
import { RichText } from "~/features/ui/rich-text";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type HeadlineBBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  eyebrow?: string | null;
  hasSolidBackground?: boolean | null;
  hasSpacer?: boolean | null;
  hasTopDivider?: boolean | null;
  isHighlighted?: boolean | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  linkVariant?: string | null;
  mainHeadline?: string | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
};
export function HeadlineBBlockView({
  anchor,
  eyebrow,
  hasSolidBackground = false,
  hasSpacer = false,
  hasTopDivider = true,
  isHighlighted = false,
  link,
  linkVariant,
  mainHeadline,
  mode = "light",
  sectionProps,
  text
}: HeadlineBBlockViewProps) {
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "paddingLeft": "var(--spacing-xl)",
    "paddingRight": "var(--spacing-xl)",
    "paddingTop": "var(--spacing-xl)",
    "paddingBottom": "var(--spacing-xl)",
    "color": "var(--text-primary)",
    "@media (min-width: 1024px)": {
      "paddingLeft": "var(--spacing-3xl)",
      "paddingRight": "var(--spacing-3xl)",
      "paddingTop": "var(--spacing-2xl)",
      "paddingBottom": "var(--spacing-2xl)"
    }
  }, {
    "backgroundColor": "var(--bg-primary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, hasSpacer && {
    "paddingTop": "var(--spacing-4xl) !important"
  }, hasSolidBackground && {
    "position": "relative",
    "zIndex": "10"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <_StyledGrid $_css2={[{
        "display": "grid",
        "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
        "gap": "var(--spacing-lg)",
        "@media (min-width: 1024px)": {
          "gap": "var(--spacing-3xl)"
        }
      }]}>
          <_StyledDiv $_css3={[{
          "position": "relative",
          "zIndex": "10",
          "gridColumn": "1 / -1",
          "display": "flex",
          "flexDirection": "column"
        }, isLinkValid(cta) && {
          "@media (min-width: 1024px)": {
            "gridColumn": "span 6 / span 6"
          }
        }]}>
            {eyebrow && <Eyebrow text={eyebrow} mode={mode as Mode} />}
            {mainHeadline && <_StyledHeadline type="h2" text={mainHeadline} />}
            {text}
          </_StyledDiv>
          {isLinkValid(cta) && <_StyledDiv2>
              <Button as={Link} {...cta} icon={icon} variant={linkVariant as ButtonVariant} />
            </_StyledDiv2>}
        </_StyledGrid>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1i9e9if-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-1i9e9if-1"
})({
  "borderWidth": "0px"
});
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-1i9e9if-2"
})`${p => p.$_css2}`;
var _StyledDiv = _styled4("div").withConfig({
  componentId: "sc-sf-1i9e9if-3"
})`${p => p.$_css3}`;
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-1i9e9if-4"
})({
  "marginTop": "var(--spacing-sm)",
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "4.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.044rem",
    "fontWeight": "400"
  }
});
var _StyledDiv2 = /*#__PURE__*/_styled6("div").withConfig({
  displayName: "headline-b___StyledDiv2",
  componentId: "sc-3i06zi-0"
})({
  "gridColumn": "span 6 / span 6",
  "display": "flex",
  "alignItems": "center",
  "justifyContent": "flex-start",
  "@media (min-width: 1024px)": {
    "justifyContent": "flex-end"
  }
});